import React, { useEffect, useState, useRef } from "react";
import { FaUserCircle, FaBell, FaSignOutAlt, FaTimes, FaCheckCircle } from "react-icons/fa";
import { HiOutlineMenu, HiOutlineOfficeBuilding } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import Profile from "./Profile/Profile";
import Cookies from "js-cookie";
import { ToastContainer, toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

// ✅ Fallback avatar - no external dependency
const FALLBACK_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23e2e8f0'/%3E%3Ctext x='75' y='75' font-size='50' text-anchor='middle' dy='.3em' fill='%2394a3b8'%3E👤%3C/text%3E%3C/svg%3E";

const Header = ({ toggleSidebar }) => {
  const [isUserPopupVisible, setIsUserPopupVisible] = useState(false);
  const [isNotificationPopupVisible, setIsNotificationPopupVisible] = useState(false);
  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [profilePic, setProfilePic] = useState(FALLBACK_AVATAR);
  const [profileName, setProfileName] = useState("Admin User");
  const [collegeName, setCollegeName] = useState("");
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(true);
  
  const userPopupRef = useRef(null);
  const notificationPopupRef = useRef(null);
  const userButtonRef = useRef(null);
  const notificationButtonRef = useRef(null);
  
  const navigate = useNavigate();
  const authToken = Cookies.get("authToken");

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle notification popup
      if (
        isNotificationPopupVisible &&
        notificationPopupRef.current &&
        !notificationPopupRef.current.contains(event.target) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(event.target)
      ) {
        setIsNotificationPopupVisible(false);
      }
      
      // Handle user popup
      if (
        isUserPopupVisible &&
        userPopupRef.current &&
        !userPopupRef.current.contains(event.target) &&
        userButtonRef.current &&
        !userButtonRef.current.contains(event.target)
      ) {
        setIsUserPopupVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isNotificationPopupVisible, isUserPopupVisible]);

  // Fetch profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!authToken) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAdminProfile`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        
        if (data?.data) {
          const adminData = data.data;
          setCollegeName(adminData.collegeName || "");
          setProfilePic(adminData.image || FALLBACK_AVATAR);
          setProfileName(adminData.fullName || "Admin User");
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        // Don't show error toast for profile fetch failure
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [authToken]);

  const toggleUserPopup = () => {
    setIsUserPopupVisible(!isUserPopupVisible);
    if (isNotificationPopupVisible) setIsNotificationPopupVisible(false);
  };

  const toggleNotificationPopup = () => {
    setIsNotificationPopupVisible(!isNotificationPopupVisible);
    if (isUserPopupVisible) setIsUserPopupVisible(false);
  };

  const handleLogout = () => {
    Cookies.remove("authToken", { path: "/" });
    toast.success("Logout successful! Redirecting...", {
      position: "top-right",
      autoClose: 2000,
      icon: '👋',
    });
    setTimeout(() => navigate("/login"), 2000);
  };

  const handleSaveProfileChanges = (newProfilePic, newProfileName) => {
    setProfilePic(newProfilePic || FALLBACK_AVATAR);
    setProfileName(newProfileName);
    setIsProfileModalVisible(false);
    toast.success("Profile updated successfully", {
      position: "top-right",
      autoClose: 3000,
      icon: '✅',
    });
  };

  const handleProfile = () => {
    setIsProfileModalVisible(true);
    setIsUserPopupVisible(false);
  };

  const clearNotifications = () => {
    setNotifications([]);
    toast.success("All notifications cleared", {
      position: "top-right",
      autoClose: 2000,
      icon: '🗑️',
    });
  };

  const closeProfileModal = () => {
    setIsProfileModalVisible(false);
  };

  const handleSeeAllNotifications = () => {
    navigate("/notifications");
    setIsNotificationPopupVisible(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setProfilePic(FALLBACK_AVATAR);
  };

  return (
    <>
      <header className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 text-white px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 shadow-xl sticky top-0 z-40">
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        
        <div className="flex items-center justify-between w-full">
          {/* Left section with toggle button and college name */}
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            {/* Toggle button for mobile */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={toggleSidebar}
              className="md:hidden p-1.5 rounded-lg hover:bg-slate-700/50 transition-colors duration-200 flex-shrink-0"
              aria-label="Toggle sidebar"
            >
              <HiOutlineMenu className="w-5 h-5" />
            </motion.button>

            {/* College Name with Loading State */}
            <div className="flex items-center space-x-2 min-w-0">
              <HiOutlineOfficeBuilding className="hidden sm:block w-4 h-4 text-slate-400 flex-shrink-0" />
              {loading ? (
                <div className="h-5 w-32 bg-slate-700 rounded animate-pulse"></div>
              ) : (
                <h1 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold truncate text-slate-200">
                  {collegeName || "College Management"}
                </h1>
              )}
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 flex-shrink-0">
            {/* Notifications */}
            <div className="relative">
              <motion.button
                ref={notificationButtonRef}
                whileTap={{ scale: 0.95 }}
                onClick={toggleNotificationPopup}
                className="p-1.5 sm:p-2 rounded-full hover:bg-slate-700/50 transition-colors duration-200 relative group"
                aria-label="Notifications"
              >
                <FaBell className="text-slate-300 text-sm sm:text-base md:text-lg group-hover:text-amber-300 transition" />
                <AnimatePresence>
                  {notifications.length > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] sm:text-xs min-w-[16px] sm:min-w-[18px] h-4 sm:h-5 flex items-center justify-center rounded-full px-1 shadow-lg"
                    >
                      {notifications.length}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>

              <AnimatePresence>
                {isNotificationPopupVisible && (
                  <>
                    {/* Backdrop for mobile */}
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/20 sm:hidden z-40"
                      onClick={() => setIsNotificationPopupVisible(false)}
                    />
                    
                    {/* Notification Dropdown */}
                    <motion.div
                      ref={notificationPopupRef}
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 bg-white/95 backdrop-blur-md text-gray-800 rounded-xl shadow-2xl w-[280px] xs:w-72 sm:w-80 z-50 border border-slate-200 overflow-hidden"
                    >
                      <div className="p-3 sm:p-4 border-b border-slate-200 flex justify-between items-center">
                        <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                          Notifications
                        </h3>
                        {notifications.length > 0 && (
                          <button
                            onClick={clearNotifications}
                            className="text-xs text-amber-600 hover:text-amber-700 font-medium transition-colors"
                          >
                            Clear All
                          </button>
                        )}
                      </div>

                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length > 0 ? (
                          <>
                            {notifications.slice(0, 5).map((notification, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="p-3 hover:bg-amber-50 border-b border-slate-100 last:border-b-0 cursor-pointer transition-colors"
                                onClick={handleSeeAllNotifications}
                              >
                                <p className="text-xs sm:text-sm text-gray-700 line-clamp-2">
                                  {notification}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {Math.floor(Math.random() * 60)} minutes ago
                                </p>
                              </motion.div>
                            ))}

                            {notifications.length > 5 && (
                              <div className="p-3 text-center border-t border-slate-200">
                                <button
                                  onClick={handleSeeAllNotifications}
                                  className="text-amber-600 hover:text-amber-800 text-xs sm:text-sm font-medium transition-colors"
                                >
                                  View All ({notifications.length})
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="p-6 sm:p-8 text-center"
                          >
                            <FaBell className="w-8 h-8 sm:w-10 sm:h-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-xs sm:text-sm">No new notifications</p>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            {/* User Profile */}
            <div className="relative">
              <motion.button
                ref={userButtonRef}
                whileTap={{ scale: 0.95 }}
                onClick={toggleUserPopup}
                className="flex items-center space-x-1 sm:space-x-2 p-1 sm:p-2 rounded-full hover:bg-slate-700/50 transition-colors duration-200 group"
                aria-label="Profile menu"
              >
                <div className="relative">
                  {imageLoading && (
                    <div className="absolute inset-0 rounded-full bg-slate-700 animate-pulse" />
                  )}
                  <img
                    src={profilePic}
                    alt={profileName}
                    className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full object-cover border-2 border-amber-400/50 group-hover:border-amber-400 transition"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                </div>
                <span className="hidden sm:inline-block text-xs sm:text-sm md:text-base font-medium text-slate-200 group-hover:text-white max-w-[80px] md:max-w-[120px] truncate">
                  {profileName}
                </span>
              </motion.button>

              <AnimatePresence>
                {isUserPopupVisible && (
                  <>
                    {/* Backdrop for mobile */}
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/20 sm:hidden z-40"
                      onClick={() => setIsUserPopupVisible(false)}
                    />
                    
                    {/* Profile Dropdown */}
                    <motion.div
                      ref={userPopupRef}
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 bg-white/95 backdrop-blur-md text-gray-800 rounded-xl shadow-2xl w-[260px] xs:w-72 z-50 border border-slate-200 overflow-hidden"
                    >
                      <div className="p-4 sm:p-5 text-center border-b border-slate-200">
                        <div className="relative inline-block">
                          <img
                            src={profilePic}
                            alt={profileName}
                            className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover mx-auto border-3 border-amber-400 mb-2"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = FALLBACK_AVATAR;
                            }}
                          />
                          <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                        </div>
                        <h4 className="font-semibold text-gray-800 text-sm sm:text-base truncate px-2">
                          {profileName}
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5">Administrator</p>
                      </div>

                      <div className="p-3 space-y-2">
                        <motion.button
                          whileHover={{ x: 5 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleProfile}
                          className="w-full px-3 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-lg flex items-center justify-center space-x-2 transition-all text-sm shadow-md hover:shadow-lg"
                        >
                          <FaUserCircle className="text-sm" />
                          <span>Edit Profile</span>
                        </motion.button>

                        <motion.button
                          whileHover={{ x: 5 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleLogout}
                          className="w-full px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm"
                        >
                          <FaSignOutAlt className="text-sm" />
                          <span>Log Out</span>
                        </motion.button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Modal */}
      <AnimatePresence>
        {isProfileModalVisible && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50"
            onClick={closeProfileModal}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800">
                  Edit Profile
                </h3>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={closeProfileModal}
                  className="text-slate-500 hover:text-slate-700 text-2xl sm:text-3xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
                  aria-label="Close"
                >
                  &times;
                </motion.button>
              </div>
              <div className="p-4 sm:p-6">
                <Profile onSave={handleSaveProfileChanges} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;